/**
 * Machine-discovered projects: what `viko project add` registered on a machine, published to
 * the account ONLY inside the encrypted `machine` report stream (`machine.projects` frame), and
 * the `project.register` command result. Names, paths and configuration never leave as plaintext.
 */
import { z } from 'zod';
import { MachineCandidate, MachineCheckout, MachineProjectDefaults } from './account-projects.js';
import { ContentBase64Url } from './content.js';
import { TendencyFacts } from './project-setup.js'; // g5-setup
const Text = (max) => z.string().min(1).max(max);
const Ts = z.number().int().nonnegative().max(Number.MAX_SAFE_INTEGER);
/** Largest `viko.project.yaml` a report carries (the web import limit is 128 KB). */
export const MACHINE_PROJECT_YAML_BYTES = 64_000;
export const MachineProjectLane = z.strictObject({
    id: Text(128),
    target: Text(128),
    production: z.boolean(),
});
export const MachineProject = z.strictObject({
    /** `project.id` in the local viko.project.yaml. */
    localId: Text(256),
    name: Text(256),
    repoPath: Text(4096),
    repoName: Text(256),
    defaultBranch: Text(256),
    lanes: z.array(MachineProjectLane).max(32),
    /** SHA-256 (base64url) of the YAML text; changes whenever the local file changes. */
    configDigest: ContentBase64Url.length(43),
    /** Absent only when the file is larger than `MACHINE_PROJECT_YAML_BYTES`. */
    yaml: z.string().max(MACHINE_PROJECT_YAML_BYTES).optional(),
});
export const MachineProjectProblem = z.strictObject({
    repoPath: Text(4096),
    repoName: Text(256),
    error: Text(1000),
});
export const MachineProjectsFrame = z.strictObject({
    type: z.literal('machine.projects'),
    ts: Ts,
    /** This machine's id in viko.project.yaml (`machines.allowed`, `repos[].paths`). */
    machine: Text(128),
    projects: z.array(MachineProject).max(64),
    /** Registered checkouts whose configuration is missing or invalid. */
    problems: z.array(MachineProjectProblem).max(32),
    // account-projects (frame v2, additive): account checkouts, unbound repos, learned defaults.
    checkouts: z.array(MachineCheckout).max(128).optional(),
    candidates: z.array(MachineCandidate).max(64).optional(),
    defaults: MachineProjectDefaults.optional(),
    // ---- g5-setup: per-repo facts for learned tendencies (instruction files, branch naming) ----
    tendencyFacts: z.array(TendencyFacts).max(128).optional(),
    // ---- end g5-setup ----
});
/** Human-readable onboarding draft for a repository without viko.project.yaml. */
export const ProjectDraftSummary = z.strictObject({
    repoPath: Text(4096),
    localId: Text(256),
    name: Text(256),
    defaultBranch: Text(256),
    test: z.array(z.string().max(1000)).max(16),
    build: z.array(z.string().max(1000)).max(16),
    lanes: z.array(MachineProjectLane).max(32),
    assumptions: z.array(z.string().max(1000)).max(32),
    questions: z.array(z.string().max(1000)).max(32),
    /** Digest of the exact YAML the daemon will write; echo it back to confirm. */
    digest: ContentBase64Url.length(43),
});
export const ProjectRegisterResult = z.discriminatedUnion('status', [
    z.strictObject({ status: z.literal('registered'), project: MachineProject }),
    z.strictObject({ status: z.literal('needs-config'), draft: ProjectDraftSummary }),
]);
//# sourceMappingURL=machine-projects.js.map